import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../environments/environments';
import { ApiVenue, VenueListExtended } from '../models/venue-admin.model';
import { Performance, PerformanceCreateRequest } from '../models/performance.model';
import {
  ScanTestBarcode,
  ScanTestMasterConfig,
  ScanTestRecord,
  RunTestStartResponse,
  RunTestStatusResponse
} from '../models/scan-testing.model';

const apiUrl = environment.apiEndpoint;

@Injectable({
  providedIn: 'root'
})

export class Apiservice {

  constructor(private _http:HttpClient) {
   }


  getVenue(): Observable<Record<string, ApiVenue>> {

    return this._http.get<Record<string, ApiVenue>>(apiUrl + '/venue?version=2');
  }

  /**
   * Returns venues that have recorded a scan at or after `since` (UTC epoch
   * seconds), as a map of venueId -> lastScanEpoch. Only includes venues the
   * authenticated user is permitted to see; an empty object is valid.
   * See docs/activeScan.md.
   */
  getActiveScans(since: number): Observable<Record<string, number>> {
    return this._http.get<Record<string, number>>(apiUrl + '/activeScans?since=' + since);
  }

  getAttendance(venueid: string | undefined, date: string | undefined , endDate: string | undefined){

    return  this._http.get(apiUrl + '/attendance?venue=' + venueid + '&date=' + date + '&table=True' + '&endDate=' + endDate);
  }

  getDeliveryFeeds(venueid: string){

    return  this._http.get('https://57hb9ue4x9.execute-api.ap-southeast-2.amazonaws.com/prod/delivery?venue=' + venueid);
  }

  postDeliveryFeeds(venueid: string, body: any){
      
      return  this._http.post('https://57hb9ue4x9.execute-api.ap-southeast-2.amazonaws.com/prod/delivery?venue=' + venueid, body);
  }

  getDeliveryOptions(){

    return  this._http.get(apiUrl + '/deliveryOptions');
  }

  getLiveAttendance(venueid: string | undefined, date: Date | undefined){

    return  this._http.get(apiUrl + '/attendance?venue=' + venueid + '&date=' + date + '&table=True');
  }

  getCurrentUser(){
    return this._http.get(apiUrl + '/user/')
  }

  getEditUser(){
    return this._http.get(apiUrl + '/user/&key=user_52607207-e3db-4412-9d35-bf20575555a3')
  }

  getAllUsers(){
    return this._http.get(apiUrl + '/users')
  }

  getHistory(venueid: string | undefined, barcode: string | undefined){
    return this._http.get(apiUrl + '/history?venue=' + venueid + '&barcode=' + barcode)
  }

  getGates(venueid: string | undefined){
    return this._http.get(apiUrl + '/gates?venue=' + venueid)
  }


  getScanHistoryV2(venueid: string | undefined , barcode: string | undefined, date: string, endDate: string | undefined, section: string | undefined, row: string | undefined, seat: string | undefined){
    return this._http.get(apiUrl + '/scanHistory?venue=' + venueid +"&barcode=" + barcode + "&date=" + date + "&endDate=" +endDate + "&section=" + section + "&row=" + row + "&seat=" + seat)
  }

  getASTAT(venueid: string | undefined, date: string | undefined, options?: any): Observable<string> {
    const apiUrl = 'https://57hb9ue4x9.execute-api.ap-southeast-2.amazonaws.com/prod/astat';
    
  
    const urlWithParams = `${apiUrl}?venue=${venueid}&date=${date}`;

  
    return this._http.get(urlWithParams, { responseType: 'text' });
  }

  getShareLink(venueid: string | undefined, date: string){
    return this._http.get(apiUrl + '/share?venue=' + venueid + '&date=' + date)
  }


  getScanDates(venueid: string | undefined){ 
    return this._http.get(apiUrl + '/venuedates?venue=' + venueid)
  }

  getSessions(venueid: string | undefined, date: string | undefined){
    return this._http.get(apiUrl + '/sessions?venue=' + venueid + '&date=' + date)
  }

  postSessions(body: { key: string; sessions: Array<{ key: string; startEpoch: number; endEpoch: number; name?: string }> }) {
    return this._http.post(apiUrl + '/sessions', body, { responseType: 'text' });
  }

  getScheduleDelivery(venue: string, date: string) {
    return this._http.get(apiUrl + '/scheduleDelivery?venue=' + venue + '&date=' + date);
  }

  postScheduleDelivery(body: {
    venue: string;
    date: string;
    emails: string[];
    scheduleId?: number;
    name?: string;
    sendType?: 'interval' | 'fixed';
    frequency?: number;
    startTime?: string;
    endTime?: string;
    sendTime?: string;
  }) {
    return this._http.post(apiUrl + '/scheduleDelivery', body);
  }

  deleteScheduleDelivery(venue: string, date: string, params: { scheduleId?: number; email?: string }) {
    let url = apiUrl + '/scheduleDelivery?venue=' + venue + '&date=' + date;
    if (params.scheduleId != null) { url += '&scheduleId=' + params.scheduleId; }
    if (params.email) { url += '&email=' + encodeURIComponent(params.email); }
    return this._http.delete(url);
  }

  getVenueAdmin(): Observable<VenueListExtended> {
    return this._http.get<VenueListExtended>(apiUrl + '/venueAdmin');
  }

  postVenueAdmin(body: { primaryVenue?: string; venues: VenueListExtended['venues'] }) {
    return this._http.post(apiUrl + '/venueAdmin', body);
  }

  getPerformances(venueid: string, start?: number, end?: number): Observable<Performance[]> {
    let url = apiUrl + '/performance?venue=' + encodeURIComponent(venueid);
    if (start != null) { url += '&start=' + start; }
    if (end != null) { url += '&end=' + end; }
    return this._http.get<Performance[]>(url);
  }

  postPerformance(body: PerformanceCreateRequest): Observable<Performance> {
    return this._http.post<Performance>(apiUrl + '/performance', body);
  }

  deletePerformance(venueid: string, timestamp: number) {
    return this._http.delete(apiUrl + '/performance?venue=' + encodeURIComponent(venueid) + '&timestamp=' + timestamp);
  }

  getScanTests(venueid: string, performanceItem: number): Observable<ScanTestRecord[]> {
    return this._http.get<ScanTestRecord[]>(apiUrl + '/tests?venue=' + encodeURIComponent(venueid) + '&performanceTimestamp=' + performanceItem);
  }

  postScanTest(body: ScanTestMasterConfig | ScanTestBarcode): Observable<ScanTestRecord> {
    return this._http.post<ScanTestRecord>(apiUrl + '/tests', body);
  }

  deleteScanTests(key: string, barcode?: string) {
    let url = apiUrl + '/tests?key=' + encodeURIComponent(key);
    if (barcode) { url += '&barcode=' + encodeURIComponent(barcode); }
    return this._http.delete(url);
  }

  startScanTestRun(key: string, item?: number): Observable<RunTestStartResponse> {
    const body: { key: string; item?: number } = { key };
    if (item != null) { body.item = item; }
    return this._http.post<RunTestStartResponse>(apiUrl + '/runTest', body);
  }

  getScanTestRun(key: string, runId?: string): Observable<RunTestStatusResponse> {
    let url = apiUrl + '/runTest?key=' + encodeURIComponent(key);
    if (runId) { url += '&runId=' + encodeURIComponent(runId); }
    return this._http.get<RunTestStatusResponse>(url);
  }

}
